<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import Link from "../components/Link.svelte";
	import Icon from "../components/Icon.svelte";
	import ProductTypeCard from "../components/ProductTypeCard.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import { getTenant } from "../getTenant.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { makeInflect } from "../../../core/utils/makeInflect.js";
	import { getProductDetailPath } from "../../../core/schema/paths/getProductDetailPath.js";
	import translations from "./ProductsByType.translations.json";
	import type { ProductsByTypeProps } from "./ProductsByTypeProps.js";

	type $$Props = ProductsByTypeProps;

	const translate = getTranslate(translations);
	const tenant = getTenant();

	const { headline, longDescription, products, otherCategories, breadcrumbs } = $$restProps as $$Props;

	const productsAnchor = "products";

	const productsInCategoryKey = makeInflect("productsInCategoryOne", "productsInCategoryFew", "productsInCategoryMany");
</script>

<Layout>
	<section class="border-lttertiary/70 !mb-0 border-b pb-16 md:pb-24">
		<div class="container">
			<div class="mx-4 md:mx-0">
				<nav aria-label="breadcrumb" class="font-lexend flex flex-wrap items-center gap-x-4 gap-y-3 md:mx-16 xl:mx-32">
					<span class="bg-ltprimary-200 mr-4 h-px w-8 shrink-0" aria-hidden="true"></span>
					{#each breadcrumbs as { url, label }, index (url)}
						{#if index > 0}
							<span class="text-base/none font-light uppercase text-[#CDC7FF]" aria-hidden="true">/</span>
						{/if}
						<a
							href={url}
							class="text-ltprimary-200 text-base/none font-light uppercase"
							aria-current={index === breadcrumbs.length - 1 ? "page" : undefined}
						>
							{nbspify(label)}
						</a>
					{/each}
				</nav>

				<div class="mt-8 flex flex-col gap-8 md:mx-16 md:mt-16 md:gap-12 xl:mx-32 xl:flex-row xl:items-start xl:gap-32">
					<div class="flex-1 xl:max-w-3xl">
						<h1 class="text-ltprimary p-0 font-light uppercase tracking-wider">
							{nbspify(headline[tenant.locale])}
						</h1>
					</div>
					<div class="flex flex-1 flex-col gap-8 xl:max-w-3xl xl:gap-16">
						<p class="text-ltsecondary font-lexend !text-md/normal !mb-0 font-light tracking-[0.02em]">
							{nbspify(longDescription[tenant.locale])}
						</p>
						<div>
							<Link asButton variant="secondary" icon={arrowRight} href="#{productsAnchor}">
								{translate("showProducts")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Products count bar -->
	<section id={productsAnchor} class="border-lttertiary/70 !my-0 border-b px-4 py-10 md:px-16 xl:px-32">
		<div class="flex flex-col justify-between gap-4 md:flex-row md:items-center md:gap-8">
			<span class="font-lexend text-[1.375rem]/[1.6] uppercase tracking-[0.03em]">
				<span class="text-ltprimary-300 font-bold">{products.length}</span>
				<span class="text-ltprimary font-light">{translate(productsInCategoryKey(products.length))}</span>
			</span>
			<span class="text-ltsecondary-200 text-2xs font-light tracking-[0.02em]">
				{translate("sortInfo")}
			</span>
		</div>
	</section>

	<!-- Products grid -->
	<section class="!my-0 px-4 pb-0 pt-8 md:px-16 md:pt-10 xl:px-32">
		{#if products.length > 0}
			<div class="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
				{#each products as product (product.id)}
					<ProductTypeCard
						{product}
						href="/{getProductDetailPath(product.category, product)}"
						detailLabel={translate("productDetail")}
					/>
				{/each}
			</div>
		{:else}
			<p class="text-ltsecondary py-8 text-center text-xs">{translate("noProducts")}</p>
		{/if}
	</section>

	{#if otherCategories.length > 0}
		<section class="!my-0 py-20">
			<div class="mb-6 flex items-center gap-4 px-4 md:px-16 xl:px-32">
				<span class="bg-ltprimary-200 h-px w-8"></span>
				<span class="text-ltprimary-200 text-xs uppercase tracking-wider">
					{translate("otherCategories")}
				</span>
			</div>
			<div class="border-lttertiary/70 border-y">
				<div class="px-4 md:px-16 xl:px-32">
					<ul class="border-lttertiary grid grid-cols-1 border-l md:grid-cols-2 lg:grid-cols-4">
						{#each otherCategories as otherCategory (otherCategory.href[tenant.locale])}
							<li class="border-lttertiary border-b border-r last:border-b-0 md:border-b-0">
								<a
									href="/{otherCategory.href[tenant.locale]}"
									class="hover:text-ltprimary-300 text-ltprimary group flex h-full items-center justify-between gap-4 px-6 py-5 text-xs font-normal uppercase tracking-wider transition-colors"
								>
									<span>{otherCategory.name[tenant.locale]}</span>
									<Icon
										icon={arrowRight}
										class="text-ltprimary-300 size-3 shrink-0 transition-transform group-hover:translate-x-1"
									/>
								</a>
							</li>
						{/each}
					</ul>
				</div>
			</div>
		</section>
	{/if}

	<section class="!my-0">
		<ContactForm />
	</section>
</Layout>
